/**
 * Sidebar — v4 (Spec §2.1, Mai 2026).
 *
 * Layout (Top → Bottom):
 *   1. WorkspaceSwitcher (48px) — Combobox mit Suche + "+ Neuer Workspace"
 *   2. Quick-Action "+ Beleg hochladen" (Brand-Filled, full-width, Shortcut N)
 *   3. Primary-Nav: Dashboard, Inbox (Badge), Rechnungen, Workflows, Lieferanten, Payment-Runs
 *   4. Section-Trenner
 *   5. Secondary "Analyse": Analytics, Cashflow, Reports
 *   6. Pinned-Items (drag-reorder, max 8)
 *   7. Spacer
 *   8. Footer: Settings, Hilfe, Notifications-Bell
 *   9. UserMenu (56px) — Dropdown nach oben
 *
 * Spec-konform:
 *  - Width: 248px expanded / 56px collapsed
 *  - Item-Density: 32px Hoehe, 14px Body, 16px Icon, 8px Gap
 *  - Active: bg-bg-active, links 2px Brand-Bar
 *  - Collapse: persistiert, Toggle-Button + `[`-Shortcut
 */
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  CreditCard,
  FileText,
  GitBranch,
  HelpCircle,
  Inbox,
  LayoutDashboard,
  Landmark,
  Plus,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  TrendingUp,
  Workflow,
} from "lucide-react";
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useMyApprovals } from "@/hooks/use-approvals";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/use-notifications";
import { useSidebarCollapsed } from "@/hooks/use-sidebar-collapsed";
import { useSidebarPins } from "@/hooks/use-sidebar-pins";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useShortcutsCheatsheet, KbdHint } from "@/components/shared";
import { formatRelative } from "@/lib/date";
import { WorkspaceSwitcher } from "@/components/layout/WorkspaceSwitcher";
import { UserMenu } from "@/components/layout/UserMenu";

interface ItemProps {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: number;
  collapsed: boolean;
  end?: boolean;
  /** Eindeutiger Suffix für motion.layoutId — verhindert Kollisionen, wenn die
   * Sidebar gleichzeitig als Desktop und im Mobile-Drawer gemountet ist. */
  layoutScope: string;
}

/**
 * SidebarItem — 32px hoch, gap-2 (8px), px-2, rounded-md (Spec §2.1).
 * Active: bg-bg-active text-brand-700 + 2px Brand-Bar links via motion.layoutId.
 * Collapsed: nur Icon + Tooltip rechts.
 */
function SidebarItem({ to, label, icon, badge, collapsed, end, layoutScope }: ItemProps) {
  const reducedMotion = useReducedMotion();
  return (
    <TooltipProvider delayDuration={collapsed ? 80 : 700}>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                "relative flex h-8 items-center gap-2 rounded-md text-body text-text-secondary",
                "transition-colors duration-fast ease-out-quint",
                "hover:bg-bg-hover hover:text-text-primary",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-subtle",
                collapsed ? "justify-center px-0" : "px-2",
                isActive && "bg-bg-active text-brand-700 hover:bg-bg-active hover:text-brand-700",
              )
            }
            aria-label={collapsed ? label : undefined}
          >
            {({ isActive }) => (
              <>
                {/* 2px Brand-Bar links — full-height (Spec §2.1).
                 * slidet via layoutId zwischen aktiven Items. */}
                {isActive && (
                  <motion.div
                    layoutId={`sidebar-active-bar-${layoutScope}`}
                    className="absolute -left-2 top-1 bottom-1 w-[2px] rounded-r-full bg-brand-600"
                    transition={
                      reducedMotion
                        ? { duration: 0 }
                        : { type: "spring", stiffness: 380, damping: 32 }
                    }
                  />
                )}
                <span className="flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">
                  {icon}
                </span>
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{label}</span>
                    {badge !== undefined && badge > 0 && (
                      <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 text-tiny font-semibold text-white">
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </NavLink>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right" sideOffset={8}>
            <span className="flex items-center gap-2">
              {label}
              {badge !== undefined && badge > 0 && (
                <span className="rounded-full bg-brand-600 px-1.5 text-tiny font-semibold text-white">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

interface SectionHeaderProps {
  label: string;
  collapsed: boolean;
}

function SectionHeader({ label, collapsed }: SectionHeaderProps) {
  if (collapsed) return null;
  return (
    <h3 className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-tertiary">
      {label}
    </h3>
  );
}

export interface SidebarProps {
  /** "desktop" (default) oder "mobile" — bestimmt das motion.layoutId-Scope,
   * damit Active-Bar-Animationen nicht zwischen den beiden Mount-Punkten
   * kollidieren, wenn die Sidebar parallel als Desktop-Nav und im
   * Mobile-Sheet gerendert wird. */
  variant?: "desktop" | "mobile";
}

export function Sidebar({ variant = "desktop" }: SidebarProps = {}) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { collapsed, toggle } = useSidebarCollapsed();
  const { memberships } = useAuth();
  const { data: approvals = [] } = useMyApprovals(workspaceId || null);
  const { data: notifications = [] } = useNotifications();
  const { setOpen: setCheatsheetOpen } = useShortcutsCheatsheet();
  const { pins } = useSidebarPins();

  const unread = notifications.filter((n) => !n.read).length;

  const base = `/workspaces/${workspaceId}`;
  const isAdmin = memberships.some(
    (m) => m.role === "OWNER" || m.role === "ADMIN",
  );
  // Auditor-Sicht: bis es eine eigene Rolle gibt, an Admin koppeln.
  const isAuditor = isAdmin;

  const goUpload = () => {
    // Wir navigieren immer zur Rechnungs-Liste mit ?upload=1 — nur dort lebt der
    // InvoiceUpload-Dialog. Vorher wurde der Param an die aktuelle Route gehängt,
    // was auf Dashboard/Inbox/Settings nichts ausgelöst hat.
    navigate(`${base}/invoices?upload=1`);
  };

  // Spec §2.1: max 8 Pinned-Items.
  const visiblePins = pins.slice(0, 8);

  return (
    <aside
      className={cn(
        // `hidden md:flex` wäre bei Wiederverwendung im MobileSidebarSheet falsch.
        // Der AppShell-Wrapper (`<nav className="hidden md:block">`) blendet die
        // Desktop-Variante schon aus; im Drawer wird die Sidebar ohne md:-Gate
        // gerendert — daher hier kein `hidden`.
        "group/sidebar relative sticky top-0 flex h-screen flex-col border-r border-border-default bg-bg-subtle",
        "transition-[width] duration-slow ease-in-out-quint motion-reduce:transition-none",
        // Spec §2.1: 248px expanded, 56px collapsed.
        collapsed ? "w-14" : "w-[248px]",
      )}
      data-collapsed={collapsed}
    >
      {/* 1. Workspace-Switcher (48px, Spec §2.1) */}
      <div className="flex h-12 shrink-0 items-center px-3">
        <WorkspaceSwitcher collapsed={collapsed} />
      </div>

      {/* 2. Quick-Action: + Beleg hochladen (Brand-Filled, full-width, Shortcut N) */}
      <div className={cn("pb-2", collapsed ? "flex justify-center px-2" : "px-3")}>
        <button
          type="button"
          onClick={goUpload}
          aria-label="Beleg hochladen"
          title="Beleg hochladen (N)"
          className={cn(
            "group/upload inline-flex items-center gap-2 rounded-md bg-brand-600 text-white",
            "shadow-sm ring-1 ring-brand-700/20 transition-all duration-fast ease-out-quint",
            "hover:bg-brand-700 hover:shadow-md active:scale-[0.985]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-subtle",
            collapsed
              ? "h-9 w-9 justify-center px-0"
              : "h-9 w-full justify-between px-3",
          )}
        >
          <span className="flex items-center gap-2">
            <Plus className="h-4 w-4" strokeWidth={2.5} />
            {!collapsed && (
              <span className="text-body-strong">Beleg hochladen</span>
            )}
          </span>
          {!collapsed && (
            <KbdHint
              keys="N"
              size="sm"
              className="[&_kbd]:border-white/25 [&_kbd]:bg-white/10 [&_kbd]:text-white/95"
            />
          )}
        </button>
      </div>

      {/* 3. Nav-Bereiche */}
      <nav
        className="flex-1 overflow-y-auto px-3 py-2"
        aria-label="Hauptnavigation"
      >
        {/* Primary-Nav: Dashboard, Inbox, Rechnungen, Workflows, Lieferanten, Payment-Runs */}
        <div className="space-y-0.5">
          <SidebarItem
            to={`${base}/dashboard`}
            label="Dashboard"
            icon={<LayoutDashboard />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/inbox`}
            label="Inbox"
            icon={<Inbox />}
            badge={approvals.length}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/invoices`}
            label="Rechnungen"
            icon={<FileText />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/workflows`}
            label="Workflows"
            icon={<Workflow />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/workflows/workflow-resolver`}
            label="Workflow-Routing"
            icon={<GitBranch />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/suppliers`}
            label="Lieferanten"
            icon={<Building2 />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/payment-runs`}
            label="Payment-Runs"
            icon={<CreditCard />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/bank-accounts`}
            label="Bankkonten"
            icon={<Landmark />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/purchase-orders`}
            label="Bestellungen"
            icon={<ClipboardCheck />}
            collapsed={collapsed}
            layoutScope={variant}
          />
          <SidebarItem
            to={`${base}/budgets`}
            label="Budgets"
            icon={<TrendingUp />}
            collapsed={collapsed}
            layoutScope={variant}
          />
        </div>

        {/* 4. Section-Trenner (visuell durch mt-4 + Section-Header) */}
        {/* 5. Secondary "Analyse": Analytics, Cashflow, Reports */}
        <div className="mt-4">
          <SectionHeader label="Analyse" collapsed={collapsed} />
          <div className="space-y-0.5">
            <SidebarItem
              to={`${base}/analytics`}
              label="Analytics"
              icon={<BarChart3 />}
              collapsed={collapsed}
              layoutScope={variant}
            />
            <SidebarItem
              to={`${base}/analytics/cashflow`}
              label="Cashflow"
              icon={<BarChart3 />}
              collapsed={collapsed}
              layoutScope={variant}
            />
            <SidebarItem
              to={`${base}/analytics/reports`}
              label="Reports"
              icon={<ScrollText />}
              collapsed={collapsed}
              layoutScope={variant}
            />
            {isAuditor && (
              <SidebarItem
                to={`${base}/audit`}
                label="Audit-Log"
                icon={<ScrollText />}
                collapsed={collapsed}
                layoutScope={variant}
              />
            )}
            {isAuditor && (
              <SidebarItem
                to={`${base}/compliance`}
                label="Compliance"
                icon={<ShieldCheck />}
                collapsed={collapsed}
                layoutScope={variant}
              />
            )}
          </div>
        </div>

        {/* 6. Pinned-Items (nur wenn vorhanden, max 8) */}
        {visiblePins.length > 0 && (
          <div className="mt-4">
            <SectionHeader label="Pins" collapsed={collapsed} />
            <div className="space-y-0.5">
              {visiblePins.map((p) => (
                <SidebarItem
                  key={p.id}
                  to={p.href}
                  label={p.label}
                  icon={<FileText />}
                  collapsed={collapsed}
                  layoutScope={variant}
                />
              ))}
            </div>
          </div>
        )}
        {/* 7. Spacer — wird durch flex-1 auf <nav> erreicht */}
      </nav>

      {/* 8. Footer-Section: Settings, Hilfe, Notifications-Bell (Spec §2.1) */}
      <div className="space-y-0.5 border-t border-border-default px-3 py-2">
        <SidebarItem
          to={`${base}/settings`}
          label="Einstellungen"
          icon={<SettingsIcon />}
          collapsed={collapsed}
          layoutScope={variant}
        />
        <TooltipProvider delayDuration={collapsed ? 80 : 700}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setCheatsheetOpen(true)}
                aria-label="Hilfe und Tastenkürzel"
                className={cn(
                  "flex h-8 w-full items-center gap-2 rounded-md text-body text-text-secondary",
                  "transition-colors duration-fast ease-out-quint",
                  "hover:bg-bg-hover hover:text-text-primary",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  collapsed ? "justify-center px-0" : "px-2",
                )}
              >
                <HelpCircle className="h-4 w-4 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left truncate">Hilfe + Tastenkürzel</span>
                    <KbdHint keys="?" size="sm" />
                  </>
                )}
              </button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right" sideOffset={8}>
                Hilfe + Tastenkürzel
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {/* Notifications-Bell (Spec §2.1 Footer) */}
        <DropdownMenu>
          <TooltipProvider delayDuration={collapsed ? 80 : 700}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={
                      unread > 0
                        ? `Benachrichtigungen (${unread} ungelesen)`
                        : "Benachrichtigungen"
                    }
                    className={cn(
                      "relative flex h-8 w-full items-center gap-2 rounded-md text-body text-text-secondary",
                      "transition-colors duration-fast ease-out-quint",
                      "hover:bg-bg-hover hover:text-text-primary",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                      collapsed ? "justify-center px-0" : "px-2",
                    )}
                  >
                    <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
                      <Bell className="h-4 w-4" />
                      {unread > 0 && (
                        <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 rounded-full bg-brand-600 ring-2 ring-bg-subtle" />
                      )}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left truncate">Benachrichtigungen</span>
                        {unread > 0 && (
                          <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 text-tiny font-semibold text-white">
                            {unread > 99 ? "99+" : unread}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right" sideOffset={8}>
                  Benachrichtigungen
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <DropdownMenuContent
            side="right"
            align="end"
            sideOffset={12}
            className="w-80"
          >
            <DropdownMenuLabel>Benachrichtigungen</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.length === 0 && (
              <div className="p-4 text-small text-text-tertiary">
                Keine neuen Meldungen.
              </div>
            )}
            {notifications.slice(0, 6).map((n) => (
              <DropdownMenuItem
                key={n.id}
                className="flex flex-col items-start gap-0.5"
              >
                <div className="flex w-full items-center gap-2">
                  <span className="font-medium">{n.title}</span>
                  <Badge
                    variant={
                      n.level === "urgent"
                        ? "fraud"
                        : n.level === "warning"
                          ? "warning"
                          : "info"
                    }
                    className="ml-auto text-tiny"
                  >
                    {n.level}
                  </Badge>
                </div>
                <span
                  className={cn(
                    "text-tiny text-text-tertiary",
                    !n.read && "text-text-primary",
                  )}
                >
                  {n.body}
                </span>
                <span className="text-tiny text-text-tertiary">
                  {formatRelative(n.createdAt)}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* 9. UserMenu (56px, Spec §2.1) */}
      <div className="flex h-14 shrink-0 items-center border-t border-border-default px-3">
        <UserMenu collapsed={collapsed} />
      </div>

      {/* Floating Collapse-Toggle — top-center der rechten Kante.
       * Sichtbar bei Hover über Sidebar (group/sidebar). */}
      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
        title={collapsed ? "Sidebar erweitern ([ )" : "Sidebar einklappen ([ )"}
        className={cn(
          "absolute -right-3 top-6 z-10 flex h-6 w-6 items-center justify-center",
          "rounded-full border border-border-default bg-bg-surface text-text-secondary shadow-sm",
          "opacity-0 transition-opacity duration-fast ease-out-quint",
          "group-hover/sidebar:opacity-100 focus-visible:opacity-100",
          "hover:text-text-primary hover:shadow-md",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        )}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5" />
        )}
      </button>
    </aside>
  );
}
